import { AdvancedSearchFieldLiteral } from 'search/types';
import { APIFieldType, AdvancedSearchField } from '@ardoq/api-types';
import { TAGS_FIELD_NAME } from '@ardoq/data-model';
import { t, type I18nLocale } from '@ardoq/i18n';

const toObjReducer = (
  acc: AdvancedSearchFieldLiteral,
  obj: AdvancedSearchField
): AdvancedSearchFieldLiteral => ({
  ...acc,
  [obj.name]: { ...obj },
});

export const getArdoqOid = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'id',
  label: t(i18nLocale, 'defaultFields:ardoqOid', 'Ardoq OID'),
  type: APIFieldType.TEXT,
});

const getArdoqId = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'component-key',
  label: t(i18nLocale, 'defaultFields:ardoqId', 'Ardoq ID'),
  type: APIFieldType.TEXT,
});

export const getName = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'name',
  label: t(i18nLocale, 'defaultFields:name', 'Name'),
  type: APIFieldType.TEXT,
});

export const getCreated = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'created',
  label: t(i18nLocale, 'defaultFields:created', 'Created'),
  type: APIFieldType.DATE_TIME,
});

export const getLastUpdated = (
  i18nLocale: I18nLocale
): AdvancedSearchField => ({
  name: 'lastUpdated',
  label: t(i18nLocale, 'defaultFields:lastUpdated', 'Last updated'),
  type: APIFieldType.DATE_TIME,
});

const getDescription = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'description',
  label: t(i18nLocale, 'defaultFields:description', 'Description'),
  type: APIFieldType.TEXT_AREA,
});

const getDisplayText = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'displayText',
  label: t(i18nLocale, 'defaultFields:displayText', 'Display text'),
  type: APIFieldType.TEXT,
});

const getCreatedBy = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'ardoq.createdByName',
  label: t(i18nLocale, 'defaultFields:createdBy', 'Created by'),
  type: APIFieldType.TEXT,
});

const getLastModifiedBy = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'ardoq.lastModifiedByName',
  label: t(i18nLocale, 'defaultFields:updatedBy', 'Updated by'),
  type: APIFieldType.TEXT,
});

const getTags = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: TAGS_FIELD_NAME,
  label: t(i18nLocale, 'defaultFields:tags', 'Tags'),
  type: APIFieldType.TEXT,
});

const getChildCount = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'child-count',
  label: t(i18nLocale, 'defaultFields:childCount', 'Number of children'),
  type: APIFieldType.NUMBER,
});

const getRootWorkspace = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'ardoq.rootWorkspaceName',
  label: t(i18nLocale, 'defaultFields:workspace', 'Workspace'),
  type: APIFieldType.TEXT,
});

const getParent = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'ardoq.parentComponentName',
  label: t(i18nLocale, 'defaultFields:parent', 'Parent'),
  type: APIFieldType.TEXT,
});

const getTypeName = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'typeName',
  label: t(i18nLocale, 'defaultFields:type', 'Type'),
  type: APIFieldType.TEXT,
});

const getSource = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'ardoq.sourceComponentName',
  label: t(i18nLocale, 'defaultFields:source', 'Source'),
  type: APIFieldType.TEXT,
});

const getTarget = (i18nLocale: I18nLocale): AdvancedSearchField => ({
  name: 'ardoq.targetComponentName',
  label: t(i18nLocale, 'defaultFields:target', 'Target'),
  type: APIFieldType.TEXT,
});

export const getDefaultFields = (
  i18nLocale: I18nLocale
): AdvancedSearchField[] => [
  getArdoqOid(i18nLocale),
  getParent(i18nLocale),
  getChildCount(i18nLocale),
  getTypeName(i18nLocale),
  getTags(i18nLocale),
  getRootWorkspace(i18nLocale),
  getSource(i18nLocale),
  getTarget(i18nLocale),
  getName(i18nLocale),
  getLastUpdated(i18nLocale),
  getCreated(i18nLocale),
  getCreatedBy(i18nLocale),
  getLastModifiedBy(i18nLocale),
];

export const getComponentFields = (
  i18nLocale: I18nLocale
): AdvancedSearchFieldLiteral =>
  [
    getArdoqOid(i18nLocale),
    getArdoqId(i18nLocale),
    getName(i18nLocale),
    getTypeName(i18nLocale),
    getLastUpdated(i18nLocale),
    getCreated(i18nLocale),
    getCreatedBy(i18nLocale),
    getLastModifiedBy(i18nLocale),
    getDescription(i18nLocale),
    getTags(i18nLocale),
    getRootWorkspace(i18nLocale),
    getParent(i18nLocale),
    getChildCount(i18nLocale),
  ].reduce(toObjReducer, {});

export const getReferenceFields = (
  i18nLocale: I18nLocale
): AdvancedSearchFieldLiteral =>
  [
    getArdoqOid(i18nLocale),
    getName(i18nLocale),
    getTypeName(i18nLocale),
    getLastUpdated(i18nLocale),
    getCreated(i18nLocale),
    getCreatedBy(i18nLocale),
    getLastModifiedBy(i18nLocale),
    getDescription(i18nLocale),
    getDisplayText(i18nLocale),
    getTags(i18nLocale),
    getRootWorkspace(i18nLocale),
    getSource(i18nLocale),
    getTarget(i18nLocale),
  ].reduce(toObjReducer, {});

export const getWorkspaceFields = (
  i18nLocale: I18nLocale
): AdvancedSearchFieldLiteral =>
  [
    getArdoqOid(i18nLocale),
    getName(i18nLocale),
    getLastUpdated(i18nLocale),
    getCreated(i18nLocale),
    getCreatedBy(i18nLocale),
    getLastModifiedBy(i18nLocale),
    getDescription(i18nLocale),
  ].reduce(toObjReducer, {});

// Field names are stable identifiers, independent of locale.
export const DEFAULT_FIELD_NAMES: ReadonlySet<string> = new Set(
  Object.keys(getComponentFields({}))
    .concat(Object.keys(getReferenceFields({})))
    .concat(Object.keys(getWorkspaceFields({})))
);
